import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { Button } from "components/atoms";
import {
  DocumentUploadModal,
  GeneralSelectInput,
  TextInput,
  NumberInput,
  MessageTextAreaInput,
  RadioButtonGroup,
  DatePickerInput,
  YearPickerInput,
} from "components/molecules";
import { useUserData, useUpload, useFileValidation } from "hooks";
import { documentService } from "services";
import {
  GenuineStudentFormValues,
  furtherStudyPlansOptions,
  postStudyWorkDurationOptions,
  permanentResidenceInterestOptions,
  returnTimeframeOptions,
  previousProgramNonCompletionReasonOptions,
  visaTypeOptions,
  FileWithType,
} from "types";

const ALLOWED_KEYS: Record<string, Set<string>> = {
  extractedData: new Set(["genuineStudentData"]),
};

interface GSImmigrationIntentSectionProps {
  currentSubStage?: string;
  destinationCountry?: string;
}

const GSImmigrationIntentSection: React.FC<GSImmigrationIntentSectionProps> = ({
  currentSubStage = "intent",
  destinationCountry = "Australia",
}) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<GenuineStudentFormValues>();
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<{
    visaRefusalLetters?: { id: string; fileName: string };
    visaCancellation?: { id: string; fileName: string };
    previousEnrollmentLetter?: { id: string; fileName: string };
    previousProgramTranscript?: { id: string; fileName: string };
    currentVisa?: { id: string; fileName: string };
  }>({});
  const userDataHook = useUserData();
  const { userData } = userDataHook;

  const { validateFileSync, getAcceptedFileTypes, getMaxFileSize } =
    useFileValidation({
      maxFileSize: 5 * 1024 * 1024,
      enableMagicNumberValidation: false,
      sanitizeMetadata: true,
    });

  const {
    stage,
    uploadProgress,
    successMessage,
    errorMessage,
    handleFileUpload,
    handleFileRemove,
    handleError,
  } = useUpload(userDataHook, ALLOWED_KEYS, {
    enableExtraction: false,
    customUploadMessage: "Documents uploaded successfully.",
  });

  const immigrationIntentTypes = [
    "VISA_REFUSAL_LETTERS",
    "VISA_CANCELLATION_NOTICE",
    "PREVIOUS_ENROLLMENT_LETTER",
    "PREVIOUS_PROGRAM_TRANSCRIPT",
    "CURRENT_VISA",
  ];

  useEffect(() => {
    if (userData?.id) {
      documentService
        .fetchDocumentsWithMapping(immigrationIntentTypes)
        .then(setUploadedDocuments);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const getDocumentFields = (filterByStage?: string) => {
    const allDocuments = [
      {
        id: "visarefusalletters",
        label: "Visa Refusal Letters",
        description: "All visa refusal letters from any country",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.everHadVisaRefused === true,
        uploaded: !!uploadedDocuments.visaRefusalLetters,
        stage: "compliance",
      },
      {
        id: "visacancellation",
        label: "Visa Cancellation Notice",
        description: "Visa cancellation or revocation notice",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.everHadVisaCancelled === true,
        uploaded: !!uploadedDocuments.visaCancellation,
        stage: "compliance",
      },
      {
        id: "previousenrollmentletter",
        label: "Previous Enrollment Letter",
        description:
          "Official enrollment letter (CoE) from previous study in host country",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.studiedInHostBefore === true,
        uploaded: !!uploadedDocuments.previousEnrollmentLetter,
        stage: "current",
      },
      {
        id: "previousprogramtranscript",
        label: "Previous Program Transcript",
        description:
          "Official academic transcript from previous study in host country",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.studiedInHostBefore === true,
        uploaded: !!uploadedDocuments.previousProgramTranscript,
        stage: "current",
      },
      {
        id: "currentvisa",
        label: "Current Visa Document",
        description:
          "Current visa grant notice or visa label showing current status",
        accept: ".pdf,.jpg,.jpeg",
        required: values.currentlyInHostCountry === true,
        uploaded: !!uploadedDocuments.currentVisa,
        stage: "current",
      },
    ];

    if (filterByStage) {
      return allDocuments.filter(
        (doc) => doc.stage === filterByStage && doc.required,
      );
    }

    return allDocuments;
  };

  const handleUploadWithRefresh = async (files: FileWithType[]) => {
    try {
      await handleFileUpload(files);
      setTimeout(async () => {
        if (userData?.id) {
          const docs = await documentService.fetchDocumentsWithMapping(
            immigrationIntentTypes,
          );

          setUploadedDocuments(docs);
        }
      }, 1000);
      return { files, results: [] };
    } catch (error) {
      console.error("Upload failed:", error);
      return { files: [], results: [] };
    }
  };

  const renderIntentSection = () => (
    <div className="intent-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Travel & Intent</h4>
        </div>
        <div className="form-fields">
          <div className="form-field">
            <RadioButtonGroup
              name="traveledInternationally"
              label="Have you traveled internationally before?"
              value={values.traveledInternationally}
              onChange={(value) => {
                setFieldValue("traveledInternationally", value);
                setFieldTouched("traveledInternationally", true);
                if (value === false) {
                  setFieldValue("countriesVisited", null);
                  setFieldValue("alwaysCompliedVisa", null);
                }
              }}
            />
            {values.traveledInternationally === true && (
              <>
                <NumberInput
                  id="countriesVisited"
                  label="Number of countries visited:"
                  placeholder="Enter number"
                  getFieldProps={() => ({
                    value: values.countriesVisited || "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                  })}
                  error={errors.countriesVisited}
                  touched={touched.countriesVisited}
                />
                <RadioButtonGroup
                  name="alwaysCompliedVisa"
                  label="Did you always comply with visa conditions?"
                  value={values.alwaysCompliedVisa}
                  onChange={(value) => {
                    setFieldValue("alwaysCompliedVisa", value);
                    setFieldTouched("alwaysCompliedVisa", true);
                  }}
                />
              </>
            )}
          </div>

          <NumberInput
            id="expectedProgramDuration"
            label="Expected program duration (in months):"
            placeholder="Enter duration in months"
            getFieldProps={() => ({
              value: values.expectedProgramDuration || "",
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.expectedProgramDuration}
            touched={touched.expectedProgramDuration}
          />

          <div className="form-field">
            <RadioButtonGroup
              name="planFurtherStudy"
              label="Do you plan to pursue further study after this program?"
              value={values.planFurtherStudy}
              onChange={(value) => {
                setFieldValue("planFurtherStudy", value);
                setFieldTouched("planFurtherStudy", true);
                if (value === false) {
                  setFieldValue("furtherStudyType", "");
                }
              }}
            />
            {values.planFurtherStudy === true && (
              <GeneralSelectInput
                id="furtherStudyType"
                label="Type of further study:"
                options={furtherStudyPlansOptions}
                value={values.furtherStudyType || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.furtherStudyType}
                touched={touched.furtherStudyType}
              />
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="seekWorkAuthorization"
              label="Will you seek work authorization after graduation?"
              value={values.seekWorkAuthorization}
              onChange={(value) => {
                setFieldValue("seekWorkAuthorization", value);
                setFieldTouched("seekWorkAuthorization", true);
                if (value === false) {
                  setFieldValue("intendedWorkDuration", "");
                }
              }}
            />
            {values.seekWorkAuthorization === true && (
              <GeneralSelectInput
                id="intendedWorkDuration"
                label="Intended duration:"
                options={postStudyWorkDurationOptions}
                value={values.intendedWorkDuration || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.intendedWorkDuration}
                touched={touched.intendedWorkDuration}
              />
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="interestedPermanentResidence"
              label="Are you interested in permanent residence?"
              value={values.interestedPermanentResidence}
              onChange={(value) => {
                setFieldValue("interestedPermanentResidence", value);
                setFieldTouched("interestedPermanentResidence", true);
                if (value === false) {
                  setFieldValue("permanentResidenceLevel", "");
                }
              }}
            />
            {values.interestedPermanentResidence === true && (
              <GeneralSelectInput
                id="permanentResidenceLevel"
                label="Level of interest:"
                options={permanentResidenceInterestOptions}
                value={values.permanentResidenceLevel || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.permanentResidenceLevel}
                touched={touched.permanentResidenceLevel}
              />
            )}
          </div>

          <GeneralSelectInput
            id="planReturnHome"
            label="When do you plan to return to your home country?"
            options={returnTimeframeOptions}
            value={values.planReturnHome || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.planReturnHome}
            touched={touched.planReturnHome}
          />
        </div>
      </div>
    </div>
  );

  const renderComplianceSection = () => (
    <div className="compliance-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Compliance & History</h4>
        </div>
        <div className="form-fields">
          <div className="form-field">
            <RadioButtonGroup
              name="everHadVisaRefused"
              label="Have you ever had a visa refused by any country?"
              value={values.everHadVisaRefused}
              onChange={(value) => {
                setFieldValue("everHadVisaRefused", value);
                setFieldTouched("everHadVisaRefused", true);
                if (value === false) {
                  setFieldValue("visaRefusalCount", null);
                  setFieldValue("mostRecentRefusalCountry", "");
                  setFieldValue("mostRecentRefusalYear", null);
                }
              }}
            />
            {values.everHadVisaRefused === true && (
              <>
                <NumberInput
                  id="visaRefusalCount"
                  label="Number of refusals:"
                  placeholder="Enter number"
                  getFieldProps={() => ({
                    value: values.visaRefusalCount || "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                  })}
                  error={errors.visaRefusalCount}
                  touched={touched.visaRefusalCount}
                />
                <TextInput
                  id="mostRecentRefusalCountry"
                  label="Most recent country:"
                  placeholder="Enter country name"
                  getFieldProps={() => ({
                    value: values.mostRecentRefusalCountry || "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                  })}
                  error={errors.mostRecentRefusalCountry}
                  touched={touched.mostRecentRefusalCountry}
                />
                <div className="recent-refusal-year">
                  <YearPickerInput
                    id="mostRecentRefusalYear"
                    className="mostRecentRefusalYear"
                    label="Most recent year:"
                    value={values.mostRecentRefusalYear || null}
                    onChange={(date) =>
                      setFieldValue("mostRecentRefusalYear", date)
                    }
                  />
                </div>
              </>
            )}
          </div>

          <RadioButtonGroup
            name="everHadVisaCancelled"
            label="Have you ever had a visa cancelled or revoked?"
            value={values.everHadVisaCancelled}
            onChange={(value) => {
              setFieldValue("everHadVisaCancelled", value);
              setFieldTouched("everHadVisaCancelled", true);
            }}
          />

          <RadioButtonGroup
            name="everOverstayedVisa"
            label="Have you ever overstayed a visa?"
            value={values.everOverstayedVisa}
            onChange={(value) => {
              setFieldValue("everOverstayedVisa", value);
              setFieldTouched("everOverstayedVisa", true);
            }}
          />

          <RadioButtonGroup
            name="everDeported"
            label="Have you ever been deported from any country?"
            value={values.everDeported}
            onChange={(value) => {
              setFieldValue("everDeported", value);
              setFieldTouched("everDeported", true);
            }}
          />

          <RadioButtonGroup
            name="everBannedEntry"
            label="Have you ever been banned from entering a country?"
            value={values.everBannedEntry}
            onChange={(value) => {
              setFieldValue("everBannedEntry", value);
              setFieldTouched("everBannedEntry", true);
            }}
          />

          <RadioButtonGroup
            name="everWorkedWithoutAuth"
            label="Have you ever worked without proper authorization?"
            value={values.everWorkedWithoutAuth}
            onChange={(value) => {
              setFieldValue("everWorkedWithoutAuth", value);
              setFieldTouched("everWorkedWithoutAuth", true);
            }}
          />

          <RadioButtonGroup
            name="familyMemberVisaIssues"
            label="Has any family member in this application had visa issues?"
            value={values.familyMemberVisaIssues}
            onChange={(value) => {
              setFieldValue("familyMemberVisaIssues", value);
              setFieldTouched("familyMemberVisaIssues", true);
            }}
          />

          {(values.everHadVisaRefused === true ||
            values.everHadVisaCancelled === true ||
            values.everOverstayedVisa === true ||
            values.everDeported === true ||
            values.everBannedEntry === true ||
            values.everWorkedWithoutAuth === true ||
            values.familyMemberVisaIssues === true) && (
            <MessageTextAreaInput
              id="visaHistoryExplanation"
              label="Visa History Explanation (150 words max)"
              placeholder="Provide complete details of any visa refusals, cancellations, overstays, deportations, bans, or unauthorized work. Include the country, dates, circumstances, and reasons. Explain how your circumstances have changed since then."
              rows="6"
              maxLength={150}
              getFieldProps={() => ({
                value: values.visaHistoryExplanation || "",
                onChange: handleChange,
                onBlur: handleBlur,
              })}
              error={errors.visaHistoryExplanation}
              touched={touched.visaHistoryExplanation}
            />
          )}

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="10rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Immigration Documents"
        description="Please attach visa refusal letters and cancellation notices:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  const renderCurrentStatusSection = () => (
    <div className="current-status-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Current Status</h4>
        </div>
        <div className="form-fields">
          <div className="form-field">
            <RadioButtonGroup
              name="studiedInHostBefore"
              label={`Have you studied in ${destinationCountry} before?`}
              value={values.studiedInHostBefore}
              onChange={(value) => {
                setFieldValue("studiedInHostBefore", value);
                setFieldTouched("studiedInHostBefore", true);
                if (value === false) {
                  setFieldValue("completedPreviousProgram", null);
                  setFieldValue("previousProgramNonCompletionReason", "");
                }
              }}
            />
            {values.studiedInHostBefore === true && (
              <>
                <RadioButtonGroup
                  name="completedPreviousProgram"
                  label="Did you complete the program?"
                  value={values.completedPreviousProgram}
                  onChange={(value) => {
                    setFieldValue("completedPreviousProgram", value);
                    setFieldTouched("completedPreviousProgram", true);
                    if (value === true) {
                      setFieldValue("previousProgramNonCompletionReason", "");
                    }
                  }}
                />
                {values.completedPreviousProgram === false && (
                  <>
                    <GeneralSelectInput
                      id="previousProgramNonCompletionReason"
                      label="Reason for non-completion:"
                      options={previousProgramNonCompletionReasonOptions}
                      value={values.previousProgramNonCompletionReason || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.previousProgramNonCompletionReason}
                      touched={touched.previousProgramNonCompletionReason}
                    />
                    {values.previousProgramNonCompletionReason === "other" && (
                      <TextInput
                        id="otherPreviousProgramNonCompletionReason"
                        label="Please specify:"
                        placeholder="Please specify"
                        getFieldProps={() => ({
                          value:
                            values.otherPreviousProgramNonCompletionReason ||
                            "",
                          onChange: handleChange,
                          onBlur: handleBlur,
                        })}
                        error={errors.otherPreviousProgramNonCompletionReason}
                        touched={
                          touched.otherPreviousProgramNonCompletionReason
                        }
                      />
                    )}
                  </>
                )}
                <MessageTextAreaInput
                  id="previousStudyHistory"
                  label="Previous Study History (150 words max)"
                  placeholder="Provide complete history of your previous study in the host country. Include institutions attended, courses enrolled, completion status, any course or provider changes, and any study gaps longer than 2 months. Explain reasons for any changes or non-completion."
                  rows="6"
                  maxLength={150}
                  getFieldProps={() => ({
                    value: values.previousStudyHistory || "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                  })}
                  error={errors.previousStudyHistory}
                  touched={touched.previousStudyHistory}
                />
              </>
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="currentlyInHostCountry"
              label={`Are you currently in ${destinationCountry}?`}
              value={values.currentlyInHostCountry}
              onChange={(value) => {
                setFieldValue("currentlyInHostCountry", value);
                setFieldTouched("currentlyInHostCountry", true);
                if (value === false) {
                  setFieldValue("currentVisaType", "");
                  setFieldValue("currentVisaExpiry", null);
                }
              }}
            />
            {values.currentlyInHostCountry === true && (
              <>
                <GeneralSelectInput
                  id="currentVisaType"
                  label="Current visa type:"
                  options={visaTypeOptions}
                  value={values.currentVisaType || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.currentVisaType}
                  touched={touched.currentVisaType}
                />
                <div className="visa-expiry-date">
                  <DatePickerInput
                    id="currentVisaExpiry"
                    label="Visa expiry:"
                    value={values.currentVisaExpiry}
                    onChange={(date) =>
                      setFieldValue("currentVisaExpiry", date)
                    }
                  />
                </div>
              </>
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="changingVisaType"
              label="Are you changing from a non-student visa to a student visa?"
              value={values.changingVisaType}
              onChange={(value) => {
                setFieldValue("changingVisaType", value);
                setFieldTouched("changingVisaType", true);
                if (value === false) {
                  setFieldValue("visaChangeRationale", "");
                }
              }}
            />
            {values.changingVisaType === true && (
              <MessageTextAreaInput
                id="visaChangeRationale"
                label="Visa Change Rationale (150 words max)"
                placeholder="Explain why you are applying for a student visa now when you are currently on a different visa type. Detail the reasons for this change and why it represents a logical progression in your circumstances."
                rows="6"
                maxLength={150}
                getFieldProps={() => ({
                  value: values.visaChangeRationale || "",
                  onChange: handleChange,
                  onBlur: handleBlur,
                })}
                error={errors.visaChangeRationale}
                touched={touched.visaChangeRationale}
              />
            )}
          </div>

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="10rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Current Status Documents"
        description="Please attach previous enrollment letters and current visa documents:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  switch (currentSubStage) {
    case "intent":
      return renderIntentSection();
    case "compliance":
      return renderComplianceSection();
    case "current":
      return renderCurrentStatusSection();
    default:
      return renderIntentSection();
  }
};

export default GSImmigrationIntentSection;
